import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useRef, useMemo, useState, useLayoutEffect } from 'react';
import { iconStyles, VerifiedIcon, QuestionIcon } from '@paypalcorp/pp-react-icons';
import ReactDOM from 'react-dom';
import { devWarning, utilStyles, ZINDEX_TOOLTIP, hasWindow, isTouchDevice, isActiveElementIn, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
      sysBorderRadiusSm = tokens.sysBorderRadiusSm,
      sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorPrimaryMainActive = tokens.sysColorPrimaryMainActive,
      sysColorPrimaryMain = tokens.sysColorPrimaryMain,
      sysSizePointerWidthLg = tokens.sysSizePointerWidthLg,
      sysSizeTapTargetSm = tokens.sysSizeTapTargetSm,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceSm = tokens.sysSpaceSm;

  var _textStyles = textStyles(tokens),
      body = _textStyles.body;

  var _utilStyles = utilStyles(tokens),
      elevationStyles = _utilStyles.elevationStyles,
      caretStyles = _utilStyles.caretStyles,
      focusStyles = _utilStyles.focusStyles,
      screenReader = _utilStyles.screenReader;

  var _iconStyles = iconStyles(tokens),
      iconBase = _iconStyles.base,
      iconSizeXs = _iconStyles.sizeXs;

  var maxWidth = '18rem';
  var pointerHeight = math("".concat(sysSizePointerWidthLg, "/2"));
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: tooltip_base;\n    ", ";\n    visibility: hidden;\n    opacity: 0;\n    max-width: ", ";\n    word-wrap: break-word;\n    padding: ", ";\n    position: absolute;\n    border-radius: ", ";\n    background-color: ", ";\n    z-index: ", ";\n\n    ", "\n    ", "\n    ", "\n    ", "\n\n\n    &[aria-hidden='false'] {\n      visibility: visible;\n      opacity: 1;\n    }\n\n    &.pp_react_tooltip--position_left {\n      label: position_left;\n      ", "\n    }\n\n    &.pp_react_tooltip--position_right {\n      label: position_right;\n      ", "\n    }\n\n    &.pp_react_tooltip--position_bottom {\n      label: position_bottom;\n      ", "\n    }\n\n    &.pp_react_tooltip--tip_left {\n      label: tip_position_left;\n      ", "\n    }\n\n    &.pp_react_tooltip--tip_right {\n      label: tip_position_right;\n      ", "\n    }\n  "])), body({
    color: sysColorBackgroundHighContrast
  }), maxWidth, sysSpaceLg, sysBorderRadiusSm, sysColorBackgroundSurface, ZINDEX_TOOLTIP, elevationStyles.level3, caretStyles.base(sysSizePointerWidthLg), caretStyles.positionBottom(pointerHeight), caretStyles.positionCenter(sysSizePointerWidthLg), caretStyles.positionRight(pointerHeight), caretStyles.positionLeft(pointerHeight), caretStyles.positionTop(pointerHeight), caretStyles.positionShiftLeft(sysSizePointerWidthLg), caretStyles.positionShiftRight(sysSizePointerWidthLg)); // Styles for Icon

  var icon = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: tooltip-icon;\n    ", ";\n    ", ";\n    width: ", ";\n    height: ", ";\n    padding: 0;\n    display: inline-flex;\n    justify-content: center;\n    align-items: center;\n    color: ", ";\n    cursor: pointer;\n    border: 0;\n    background-color: transparent;\n    position: relative;\n\n    &:hover,\n    &:active {\n      color: ", ";\n      outline: none;\n    }\n\n    &:focus {\n      outline: none;\n      > span:first-of-type {\n        border-radius: ", ";\n        ", ";\n      }\n    }\n  "])), iconBase, iconSizeXs, sysSizeTapTargetSm, sysSizeTapTargetSm, sysColorPrimaryMain, sysColorPrimaryMainActive, sysBorderRadiusInteractive, focusStyles.focusOffsetElement); // Image Positions

  var image = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: image;\n    display: flex;\n    img {\n      max-width: ", ";\n    }\n  "])), math("".concat(maxWidth, " - 2 * ").concat(sysSpaceLg)));

  var imageAlignBottom = function (justifyImage) {
    return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    justify-content: ", ";\n    padding-top: ", ";\n  "])), justifyImage, sysSpaceSm);
  };

  var imageAlignTop = function (justifyImage) {
    return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    justify-content: ", ";\n    padding-bottom: ", ";\n  "])), justifyImage, sysSpaceSm);
  };

  var imageTopLeft = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: image_top_left;\n    ", "\n  "])), imageAlignTop('flex-start'));
  var imageTopCenter = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: image_top_center;\n    ", "\n  "])), imageAlignTop('center'));
  var imageBottomLeft = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: image_bottom_left;\n    ", "\n  "])), imageAlignBottom('flex-start'));
  var imageBottomCenter = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: image_bottom_center;\n    ", "\n  "])), imageAlignBottom('center'));
  return {
    base: base,
    icon: icon,
    screenReader: screenReader,
    image: image,
    imageTopLeft: imageTopLeft,
    imageTopCenter: imageTopCenter,
    imageBottomLeft: imageBottomLeft,
    imageBottomCenter: imageBottomCenter
  };
}

var tooltip;
var tooltipOrigin;
/* eslint-disable complexity */

var Tooltip = function (_ref) {
  var _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'Tooltip' : _ref$name,
      _ref$description = _ref.description,
      description = _ref$description === void 0 ? 'tooltip' : _ref$description,
      _ref$screenReaderText = _ref.screenReaderText,
      screenReaderText = _ref$screenReaderText === void 0 ? name : _ref$screenReaderText,
      position = _ref.position,
      verifiedIcon = _ref.verifiedIcon,
      windowId = _ref.windowId,
      windowRef = _ref.windowRef,
      className = _ref.className,
      triggerClassName = _ref.triggerClassName,
      tooltipClassName = _ref.tooltipClassName,
      containerRef = _ref.containerRef,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      imageElement = _ref.imageElement,
      _ref$imagePosition = _ref.imagePosition,
      imagePosition = _ref$imagePosition === void 0 ? 'top-left' : _ref$imagePosition;
  var wrapperRef = useRef();
  var tooltipOriginRef = useRef();
  var tooltipRef = useRef(); // If theme is not directly provided, get from themeContext

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var noop = function () {};

  var tooltipWindow;
  var containerPos;
  var scrollX;
  var scrollY;
  var width;
  var height;
  var originWidth;
  var originHeight;
  var originPos;
  var maxWidth;
  var iconSpacing;
  var caretWidth;
  var caretHeight;
  var caretSpaceHorizontal;
  var spacingOffset;
  var tooltipIconSize;

  var _useState = useState({
    isOpen: false,
    currentEvent: null
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var isOpen = state.isOpen,
      currentEvent = state.currentEvent;

  var getWrapperCurrent = function (w) {
    return (
      /* istanbul ignore next */
      w && w.current ? w.current : null
    );
  };
  /* istanbul ignore next */


  var setTooltipPosition = function (top, left) {
    var _containerPos, _containerPos2;

    var containerElm = getWrapperCurrent(containerRef);

    var _ref2 = containerElm ? [top - ((_containerPos = containerPos) === null || _containerPos === void 0 ? void 0 : _containerPos.top) - scrollY + containerElm.scrollTop, left - ((_containerPos2 = containerPos) === null || _containerPos2 === void 0 ? void 0 : _containerPos2.left) - scrollX + containerElm.scrollLeft] : [top, left],
        _ref3 = _slicedToArray(_ref2, 2),
        offsetTop = _ref3[0],
        offsetLeft = _ref3[1];

    tooltip.style.top = "".concat(offsetTop, "px");
    tooltip.style.left = "".concat(offsetLeft, "px");
    tooltip.style.maxWidth = "".concat(maxWidth, "px");
  };
  /* istanbul ignore next */


  var getTooltipBoundary = function () {
    var providedWindow = windowRef ? getWrapperCurrent(windowRef) : document.getElementById(windowId);

    if (providedWindow) {
      var pos = providedWindow.getBoundingClientRect();
      return {
        windowEl: providedWindow,
        top: pos.top,
        right: pos.right,
        bottom: pos.bottom,
        left: pos.left
      };
    }

    return {
      windowEl: hasWindow() ? window : null,
      top: 0,
      right: window.innerWidth || document.documentElement.clientWidth,
      bottom: window.innerHeight || document.documentElement.clientHeight,
      left: 0
    };
  };

  var positionTopCenter = function () {
    // tooltip bubble starts at bottom left edge of its origin (icon/button)
    // and calculates moves from there:
    var topCenterX = originPos.left - width / 2 + originWidth / 2 + scrollX;
    var topCenterY = originPos.top - height - spacingOffset + scrollY + iconSpacing;
    setTooltipPosition(topCenterY, topCenterX);
  };
  /* istanbul ignore next */


  var positionBottom = function (top, left) {
    var offsetY = top + scrollY + height + originHeight + spacingOffset * 2 - iconSpacing * 2;
    setTooltipPosition(offsetY, left);
  };

  var positionLeft = function () {
    var buttonWidth = originWidth / 2 - tooltipIconSize / 2; // tooltip bubble starts at bottom left edge of its origin (icon/button)
    // and calculates X and Y moves from there:

    var leftPosX = originPos.left - width + buttonWidth - spacingOffset + scrollX;
    var leftPosY = originPos.top - height / 2 + originHeight / 2 + scrollY;
    tooltip.classList.add('pp_react_tooltip--position_left');
    setTooltipPosition(leftPosY, leftPosX);
  };

  var positionRight = function () {
    var buttonWidth = originWidth / 2 + tooltipIconSize / 2; // tooltip bubble starts at bottom left edge of its origin (icon/button)
    // and calculates X and Y moves from there:

    var rightPosX = originPos.left + buttonWidth + spacingOffset + scrollX;
    var rightPosY = originPos.top - height / 2 + originHeight / 2 + scrollY;
    tooltip.classList.add('pp_react_tooltip--position_right');
    setTooltipPosition(rightPosY, rightPosX);
  };

  var selfPosition =
  /* istanbul ignore next */
  function () {
    positionTopCenter();
    var boundary = tooltip.getBoundingClientRect();

    if (boundary.top < tooltipWindow.top || boundary.top < 0) {
      // position: bottom
      positionBottom(boundary.top, boundary.left);
      tooltip.classList.add('pp_react_tooltip--position_bottom'); // store new boundary

      boundary = tooltip.getBoundingClientRect();
    } // note: if both left and right are out of bounds keep it centered


    if (!(boundary.left < tooltipWindow.left && boundary.left + maxWidth > tooltipWindow.right)) {
      var sideShift = caretSpaceHorizontal + caretWidth / 2;
      var offsetY = boundary.top + scrollY;
      var offsetX = boundary.left + scrollX;

      if (boundary.left < tooltipWindow.left) {
        // position: top-right, bottom-right (with left tooltip)
        offsetX = offsetX + width / 2 - sideShift;
        tooltip.classList.add('pp_react_tooltip--tip_left');
      }

      if (boundary.left + maxWidth > tooltipWindow.right) {
        // position: top-left, bottom-left (with right tooltip)
        offsetX = offsetX - width / 2 + sideShift;
        tooltip.classList.add('pp_react_tooltip--tip_right');
      }

      setTooltipPosition(offsetY, offsetX);
    }
  }; // for keyboard focus and mouseover


  var setOpen = function (evt) {
    evt.stopPropagation();
    setState({
      isOpen: true,
      currentEvent: evt.type
    });
    // eslint-disable-next-line
    onOpen && onOpen(evt, name);
  };

  var clearOpen = function (evt) {
    // istanbul ignore next
    if (evt.type === 'resize' && isTouchDevice() && isActiveElementIn(['INPUT', 'TEXTAREA'])) {
      return;
    }

    setState({
      isOpen: false,
      currentEvent: null
    }); // eslint-disable-next-line

    onClose && onClose(name);
    /* istanbul ignore else */

    if (tooltipWindow && tooltipWindow.windowEl) {
      var _tooltipWindow = tooltipWindow,
          windowEl = _tooltipWindow.windowEl;
      windowEl.removeEventListener('mousewheel', clearOpen);
    }
  };

  var setWidth = function () {
    var tooltipMaxWidthPx = getComputedStyle(tooltip, 'style').maxWidth;
    var tooltipMaxWidth = parseInt(tooltipMaxWidthPx.split('px')[0], 10);
    var winLeft = tooltipWindow.left;
    var winRight = tooltipWindow.right;
    var iconLeft = tooltipOrigin.getBoundingClientRect().left;
    var availableLeftSpace = iconLeft - winLeft + iconSpacing;
    var availableRightSpace = winRight - iconLeft - iconSpacing - tooltipIconSize;
    var halfMaxWidth = tooltipMaxWidth / 2; // if left and right of tooltip are each > than half the maxWidth, use default max width

    /* istanbul ignore next */

    if (availableLeftSpace > halfMaxWidth && availableRightSpace > halfMaxWidth) {
      maxWidth = tooltipMaxWidth;
    } else if ( // If both sides are less than half the maxwidth,
    // then tooltip bubble will be position center,
    // so use smaller side x 2 as max width since it's centered
    availableLeftSpace < halfMaxWidth && availableRightSpace < halfMaxWidth) {
      var smallerSide = availableLeftSpace < availableRightSpace ? availableLeftSpace : availableRightSpace;
      maxWidth = smallerSide * 2;
    } else {
      // take the bigger available space between the two
      var availableSpace = availableLeftSpace > availableRightSpace ? availableLeftSpace : availableRightSpace; // if it explicitly placed left/right adjust accordingly.

      if (position) {
        // Add the caret + 4px spacing
        availableSpace = availableSpace - spacingOffset - tooltipIconSize;
      } else {
        // Add the side shift dimension (since tooltip is position top/bottom with side shift)
        availableSpace += caretSpaceHorizontal + caretWidth / 2 - tooltipIconSize;
      }

      maxWidth = availableSpace > tooltipMaxWidth ? tooltipMaxWidth : availableSpace;
    }

    tooltip.style.maxWidth = "".concat(maxWidth, "px");
    var currentWidth = tooltip.getBoundingClientRect().width;
    /* istanbul ignore next */

    width = maxWidth < currentWidth ? maxWidth : currentWidth;
    height = tooltip.offsetHeight;
  };

  var positionTooltip = function () {
    tooltip = getWrapperCurrent(tooltipRef);
    tooltipOrigin = getWrapperCurrent(tooltipOriginRef);
    originPos = tooltipOrigin.getBoundingClientRect();
    var containerElm = getWrapperCurrent(containerRef);
    /* istanbul ignore next */

    containerPos = containerElm && containerElm.getBoundingClientRect();
    tooltipWindow = getTooltipBoundary();
    width = tooltip.offsetWidth;
    height = tooltip.offsetHeight;
    originWidth = tooltipOrigin.offsetWidth;
    originHeight = tooltipOrigin.offsetHeight;
    scrollX = window.scrollX || window.pageXOffset;
    scrollY = window.scrollY || window.pageYOffset; // get tooltip's pointer dimensions:

    var rootFontSizePx = hasWindow() && window.getComputedStyle(document.body).getPropertyValue('font-size') || '16px';
    var rootFontSize = parseInt(rootFontSizePx.split('px')[0], 10);
    caretWidth = 1.125 * rootFontSize;
    caretHeight = 0.75 * rootFontSize;
    caretSpaceHorizontal = 1 * rootFontSize; //16px spacing between caret and tooltip corner

    // 4px separation between caret and tooltip icon
    spacingOffset = caretHeight + 0.25 * rootFontSize;
    var targetSize = originWidth;
    tooltipIconSize = 1.5 * rootFontSize; // icon and target have different dimensions

    iconSpacing = targetSize / 2 - tooltipIconSize / 2; // set the initial width for tooltip.

    setWidth();
    var _tooltipWindow2 = tooltipWindow,
        windowEl = _tooltipWindow2.windowEl;
    windowEl.addEventListener('mousewheel', clearOpen);

    switch (position) {
      case 'left':
        positionLeft();
        break;

      case 'right':
        positionRight();
        break;

      default:
        selfPosition();
    }
  };

  var handleClickOutside = function (evt) {
    var current = getWrapperCurrent(wrapperRef);
    /* istanbul ignore next */

    if (isOpen && current && !current.contains(evt.target)) {
      evt.stopPropagation();
      clearOpen(evt);
    }
  };

  var handleKey = function (evt) {
    /* istanbul ignore next */
    var KeyCode = new KeyboardCode(evt.key || evt.keyCode);

    if (KeyCode.IS_TAB || KeyCode.IS_ESC) {
      clearOpen(evt);
    }
  };

  useLayoutEffect(function () {
    if (isOpen) {
      positionTooltip();
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKey);
      window.addEventListener('resize', clearOpen);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', clearOpen);
    }

    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', clearOpen);
    };
  });
  /* istanbul ignore next */

  var handleMouseLeave = function (evt) {
    if (tooltip && evt.relatedTarget !== tooltip) {
      evt.stopPropagation();
      clearOpen(evt);
    }
  };

  var imagePositionStyleName = 'image' + function camelCase(str) {
    return str.replace(new RegExp(/[-_]+/, 'g'), ' ').replace(new RegExp(/[^\w\s]/, 'g'), '').replace(new RegExp(/\s+(.)(\w*)/, 'g'), function (_first, second, third) {
      return "".concat(second.toUpperCase() + third.toLowerCase());
    }).replace(new RegExp(/\w/), function (s) {
      return s.toUpperCase();
    });
  }(imagePosition);

  var imagePositionCSSVal = [_styles.image, //@ts-ignore
  _styles[imagePositionStyleName]];
  var displayImage = jsx("span", {
    css: imagePositionCSSVal,
    "data-ppui": "true"
  }, imageElement);
  //@ts-ignore

  /* istanbul ignore next */
  var tooltipNode = isOpen && ReactDOM.createPortal(jsx("div", {
    ref: tooltipRef,
    css: _styles.base,
    className: tooltipClassName,
    id: name,
    role: "tooltip",
    "aria-hidden": !isOpen,
    onMouseLeave: currentEvent === 'focus' ? noop : clearOpen,
    "data-ppui": "true"
  }, imageElement && (imagePosition === 'top-center' || imagePosition === 'top-left') && displayImage, description, imageElement && (imagePosition === 'bottom-center' || imagePosition === 'bottom-left') && displayImage), getWrapperCurrent(containerRef) || (hasWindow() ? document.body : null));
  /* istanbul ignore next */

  var TooltipIcon = verifiedIcon ? VerifiedIcon : QuestionIcon;
  /* istanbul ignore next */

  var handleTouchStart = function (evt) {
    var current = getWrapperCurrent(wrapperRef);
    if (!current.contains(evt.target)) clearOpen(evt);
  };

  return jsx("span", {
    ref: wrapperRef,
    className: className,
    "data-ppui-info": "tooltip_4.8.6"
  }, jsx(React.Fragment, null, jsx("button", _extends({
    type: "button",
    ref: tooltipOriginRef,
    css: _styles.icon
  }, isOpen ? {
    'aria-describedby': name
  } : {}, {
    onMouseEnter: currentEvent === 'focus' ? noop : setOpen,
    onMouseLeave: currentEvent === 'focus' ? noop : handleMouseLeave,
    onTouchEnd: function handleTouchEnd(evt) {
      evt.preventDefault(); // Toggle the state

      if (isOpen) {
        clearOpen(evt);
        document.removeEventListener('touchstart', handleTouchStart);
      } else {
        setOpen(evt);
        document.addEventListener('touchstart', handleTouchStart);
      }
    },
    onMouseDown: function handleMouseDown(evt) {
      return evt.preventDefault();
    },
    onFocus: isTouchDevice() ? noop : setOpen,
    className: triggerClassName,
    "data-ppui": "true"
  }), jsx(TooltipIcon, {
    size: "sm",
    "aria-hidden": "true",
    "data-ppui": "true"
  }), jsx("span", {
    css: _styles.screenReader,
    "data-ppui": "true"
  }, screenReaderText)), tooltipNode));
};

export { Tooltip, styles as tooltipStyles };
